import { LogoMark } from '@/components/WelligenceLogo/WelligenceLogoSVG';
import { useAdminsPageOptions } from '@/hooks/useAdminsPageOptions';
import { LOGOUT_MUTATION } from '@/pages/Login/operations';
import { useAdminsBarStore } from '@/stores/adminsBarStore';
import { clearUserCredentials } from '@/utils/auth';
import { ADMINS_PATH, ROOT_PATH } from '@/utils/constants';
import { useMutation } from '@apollo/client';
import {
  AdminPanelSettings,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Logout,
  Public,
} from '@mui/icons-material';
import { Collapse } from '@mui/material';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useStyles } from './styles';

const NavLink = ({ to, icon: Icon, label, isExpanded, onClick, sx = {}, className }) => {
  const classes = useStyles();
  const location = useLocation();
  const isSelected = location.pathname === to;

  if (onClick) {
    return (
      <ListItemButton className={classes.listItemButton} onClick={onClick} sx={{ ...sx }}>
        <ListItemIcon
          className={isSelected ? classes.listItemIconSelected : classes.listItemIconUnselected}
        >
          <Icon />
        </ListItemIcon>
        {isExpanded && <ListItemText primary={label} />}
      </ListItemButton>
    );
  }

  return (
    <Link className={`${classes.navLink} ${className}`} to={to} style={{ ...sx }}>
      <ListItemIcon
        className={isSelected ? classes.listItemIconSelected : classes.listItemIconUnselected}
      >
        <Icon />
      </ListItemIcon>
      {isExpanded && <ListItemText primary={label} />}
    </Link>
  );
};

const ExtraOptionsLinks = () => {
  const classes = useStyles();
  const drawerIsOpen = useAdminsBarStore((store) => store.isOpen);

  const [userLogout, { loading: logoutLoading }] = useMutation(LOGOUT_MUTATION, {
    onCompleted: () => {
      clearUserCredentials();
      sessionStorage.clear();
      window.location.reload();
    },
    onError: alert,
  });

  const extraOptions = [
    {
      label: 'Admin Panel',
      icon: AdminPanelSettings,
      to: ADMINS_PATH,
    },
    {
      label: 'Welligence dashboard',
      icon: Public,
      to: ROOT_PATH,
    },
    {
      label: 'Sign Out',
      icon: Logout,
      to: ROOT_PATH,
      onClick: () => userLogout(),
    },
  ];

  return (
    <>
      {extraOptions.map(({ label, icon, to, onClick }) => (
        <ListItem key={label} disablePadding className={classes.listItem}>
          <NavLink
            to={to}
            icon={icon}
            label={label}
            isExpanded={drawerIsOpen}
            onClick={onClick}
            className={logoutLoading ? classes.listItemButtonDisabled : undefined}
          />
        </ListItem>
      ))}
    </>
  );
};

const AdminPageOptions = () => {
  const classes = useStyles();
  const adminsPageOptions = useAdminsPageOptions();
  const [expandedMenus, setExpandedMenus] = React.useState({});
  const drawerIsOpen = useAdminsBarStore((store) => store.isOpen);
  const setIsDrawerAsOpen = useAdminsBarStore((store) => store.setIsOpen);

  const handleToggleSubmenu = (label) => {
    setExpandedMenus((prev) => ({ ...prev, [label]: !prev[label] }));

    if (!drawerIsOpen) {
      setIsDrawerAsOpen(true);
    }
  };

  return (
    <List>
      {adminsPageOptions.map((option) => {
        if (option.hide) return null;

        if (option.isSubmenu) {
          const isExpanded = expandedMenus[option.label] || false;
          return (
            <React.Fragment key={option.label}>
              <ListItem disablePadding className={classes.listItem}>
                <ListItemButton
                  className={classes.listItemButton}
                  onClick={() => handleToggleSubmenu(option.label)}
                >
                  <ListItemIcon className={classes.listItemIconUnselected}>
                    <option.icon />
                  </ListItemIcon>
                  {drawerIsOpen && <ListItemText primary={option.label} />}
                  {drawerIsOpen && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </ListItem>

              <Collapse in={isExpanded && drawerIsOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {option.children.map((child) =>
                    child.hide ? null : (
                      <ListItem key={child.label} disablePadding className={classes.listItem}>
                        <NavLink
                          to={child.route}
                          icon={child.icon}
                          label={child.label}
                          isExpanded={drawerIsOpen}
                          sx={{ paddingLeft: '2rem' }}
                        />
                      </ListItem>
                    ),
                  )}
                </List>
              </Collapse>
            </React.Fragment>
          );
        }

        return (
          <ListItem key={option.label} disablePadding className={classes.listItem}>
            <NavLink
              to={option.route}
              icon={option.icon}
              label={option.label}
              isExpanded={drawerIsOpen}
            />
          </ListItem>
        );
      })}
    </List>
  );
};

const AdminDrawer = () => {
  const classes = useStyles();
  const drawerIsOpen = useAdminsBarStore((store) => store.isOpen);
  const toggleDrawer = useAdminsBarStore((store) => store.toggleDrawer);

  const handleDrawerToggle = () => {
    toggleDrawer();
  };

  return (
    <MuiDrawer
      variant="permanent"
      component="nav"
      className={`${classes.drawer} ${drawerIsOpen ? classes.drawerOpen : classes.drawerClosed}`}
    >
      <div className={classes.drawerHeader}>
        <Link to={ADMINS_PATH}>
          <LogoMark onlyMark={!drawerIsOpen} />
        </Link>
      </div>
      <Divider />
      <AdminPageOptions />
      <List sx={{ marginTop: 'auto', paddingTop: '20px', paddingBottom: '0' }}>
        <ExtraOptionsLinks />
        <ListItem disablePadding className={classes.listItemButtonToggle}>
          <ListItemButton className={classes.toggleButton} onClick={handleDrawerToggle}>
            {drawerIsOpen ? <ChevronLeft /> : <ChevronRight />}
          </ListItemButton>
        </ListItem>
      </List>
    </MuiDrawer>
  );
};

export { AdminDrawer };
